<template>
    <div class="chat-page">
        <!--
        Гостей на первую страничку пускаем «посмотреть»: под оверлеем виден
        обычный ChatPage (сайдбар/инпут), но все действия и запросы к чат-API
        заблокированы (см. chatApi.js, useChatAccessMock и роутер). Оверлей
        принудительно предлагает войти/зарегистрироваться — гостевого режима
        больше нет.
        -->
        <GuestOverlay v-if="!access.isAuthenticated.value" />
        <main class="chat-shell" :aria-hidden="!access.isAuthenticated.value ? 'true' : null">
            <ChatSidebar
                :open="store.state.sidebarOpen"
                :reports="reports"
                :reports-loading="reportsLoading"
                :threads="threads"
                :active-thread-id="isReportsView ? null : store.state.activeThreadId"
                :is-authenticated="access.isAuthenticated.value"
                :is-admin="isAdmin"
                :user-initials="userInitials"
                :user-name="userName"
                :user-subtitle="userSubtitle"
                :plan="access.plan.value"
                :plan-name="access.planName.value"
                :days-left="access.daysLeft.value"
                :is-trial="access.isTrial.value"
                :last-update="lastUpdate"
                @new-dialog="onNewDialog"
                @select-thread="onSelectThread"
                @delete-thread="onDeleteSavedThread"
                @load-more-threads="store.loadMoreThreads"
                @threads-search="onThreadsSearch"
                :threads-has-more="store.state.threadsMeta.has_more"
                :threads-loading-more="store.state.threadsLoadingMore"
                :threads-total="store.state.threadsMeta.total"
                @logout="onLogout"
                @open-settings="openSettings"
                @upgrade="openPricing"
                @open-report="onOpenReport"
                @open-my-shares="openMyShares"
                @open-my-exports="openMyExports"
                @open-admin="goToAdmin"
                @open-support="openSupport"
                :support-unread="supportUnread.unreadCount.value"
            />

            <div
                v-if="store.state.sidebarOpen"
                class="sidebar-backdrop"
                aria-hidden="true"
                @click="store.closeSidebar"
            />

                <ReportsContent
                    v-if="isReportsView"
                    :show-menu="true"
                    @toggle-sidebar="store.toggleSidebar"
                    @upgrade="openPricing"
                />

            <ChatConversationPanel
                v-else
                ref="panelRef"
                :title="activeThreadTitle"
                :subtitle="activeThreadSubtitle"
                :context-chips="store.state.contextChips"
                :input-chips="store.state.inputChips"
                :messages="store.state.messages"
                :is-loading-history="store.state.isLoadingHistory"
                :user-initials="userInitials"
                :user-name="userName"
                :is-streaming="store.state.isStreaming"
                :active-message-id="store.state.activeMessageId"
                :is-limit-reached="access.isLimitReached.value"
                :access-tier="access.accessTier.value"
                :access-reason="access.reason.value"
                :remaining="access.remaining.value"
                :total="access.total.value"
                :show-menu="true"
                :is-admin="isAdmin"
                :is-authenticated="access.isAuthenticated.value"
                :allow-share="access.allowShare.value"
                :allow-export="access.allowExport.value"
                :selection="store.state.selection"
                :filter-selection="store.state.filterSelection"
                @send="store.sendMessage"
                @stop="store.stopStreaming"
                @remove-chip="store.removeInputChip"
                @update-filters="store.updateFilters"
                @regenerate="onRegenerate"
                @copy="onCopy"
                @feedback="onFeedback"
                @followup="onFollowup"
                @suggest="onSuggest"
                @share="onStartShare"
                @publish="onStartPublish"
                @export="onExport"
                @rename="onRenameThread"
                @delete-thread="onDeleteThread"
                @toggle-sidebar="store.toggleSidebar"
                @upgrade="openPricing"
                @toggle-select="store.toggleSelected"
                @select-all="store.selectAllVisibleMessages"
                @cancel-selection="store.cancelSelection"
                @confirm-selection="onConfirmSelection"
                @update-card-view="onUpdateCardView"
                @edit="onEditMessage"
                @version-prev="id => store.stepMessageVersion(id, -1)"
                @version-next="id => store.stepMessageVersion(id, 1)"
            />
        </main>

        <ChatSettingsModal
            :open="isSettingsOpen"
            :initial-section="settingsSection"
            :user-info="userInfo"
            :tokens-used="access.tokensUsed.value"
            :tokens-total="access.tokensTotal.value"
            :requests-used="access.requestsUsed.value"
            :requests-total="access.requestsTotal.value"
            @close="closeSettings"
            @upgrade="onSettingsUpgrade"
            @open-tickets="onOpenTicketsFromSettings"
        />

        <ChatTicketsModal
            :open="isTicketsOpen"
            :initial-category="ticketsCategory"
            @close="closeTickets"
            @read="supportUnread.refreshUnread"
        />

        <ChatPricingModal
            :open="isPricingOpen"
            :current-plan="currentPlan"
            @close="closePricing"
            @select="onPlanSelect"
        />

        <ChatPlanConfigModal
            :open="isPlanConfigOpen"
            :plan-code="pendingPlan.code"
            :plan-name="pendingPlan.name"
            :billing-period="pendingPlan.billingPeriod"
            :region-selection-enabled="pendingPlan.regionSelectionEnabled"
            :source-selection-enabled="pendingPlan.sourceSelectionEnabled"
            @close="closePlanConfig"
            @confirm="onPlanConfigConfirm"
        />

        <ChatShareModal
            :open="shareModalOpen"
            :thread-external-id="store.state.activeThreadId"
            :message-ids="selectedServerIds"
            @close="closeShare"
            @created="onShareCreated"
        />

        <ChatPublishReportModal
            :open="publishModalOpen"
            :thread-external-id="store.state.activeThreadId"
            :message-ids="selectedServerIds"
            @close="closePublish"
            @published="onReportPublished"
        />

        <ChatExportModal
            :open="exportModalOpen"
            :thread-external-id="store.state.activeThreadId"
            :thread-title="activeThreadTitle"
            :thread-subtitle="activeThreadSubtitle"
            :message-ids="selectedServerIdsForExport"
            :is-authenticated="access.isAuthenticated.value"
            @close="closeExport"
            @exported="onExported"
        />

        <ChatMySharesModal
            :open="mySharesOpen"
            @close="mySharesOpen = false"
        />

        <ChatMyExportsModal
            :open="myExportsOpen"
            @close="myExportsOpen = false"
        />

        <ChatMessageEditModal
            :open="Boolean(editingMessage)"
            :message="editingMessage"
            :is-streaming="store.state.isStreaming"
            :apply-error="regenerateApplyError"
            :response-formats="store.state.filterSelection.responseFormats"
            @close="store.closeMessageEditor"
            @apply="onApplyMessageEdit"
            @update-card-view="onUpdateCardView"
            @version-prev="id => store.stepMessageVersion(id, -1)"
            @version-next="id => store.stepMessageVersion(id, 1)"
        />
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore as useVuexStore } from 'vuex'
import ChatSidebar from '@/components/chat/sidebar/ChatSidebar.vue'
import GuestOverlay from '@/components/chat/GuestOverlay.vue'
import ChatPricingModal from '@/components/chat/sidebar/ChatPricingModal.vue'
import ChatPlanConfigModal from '@/components/chat/sidebar/ChatPlanConfigModal.vue'
import ChatSettingsModal from '@/components/chat/sidebar/ChatSettingsModal.vue'
import ChatTicketsModal from '@/components/chat/sidebar/ChatTicketsModal.vue'
import { useSupportUnread } from '@/composables/useSupportUnread'
import { createChatPlanOrder } from '@/utils/chatApi'
import ChatConversationPanel from '@/components/chat/conversation/ChatConversationPanel.vue'
import ReportsContent from '@/components/reports/ReportsContent.vue'
import ChatMessageEditModal from '@/components/chat/conversation/ChatMessageEditModal.vue'
import ChatShareModal from '@/components/chat/share/ChatShareModal.vue'
import ChatPublishReportModal from '@/components/chat/share/ChatPublishReportModal.vue'
import ChatExportModal from '@/components/chat/share/ChatExportModal.vue'
import ChatMySharesModal from '@/components/chat/share/ChatMySharesModal.vue'
import ChatMyExportsModal from '@/components/chat/share/ChatMyExportsModal.vue'
import { useChatStore } from '@/components/chat/state/useChatStore'
import { getReports } from '@/utils/chatApi'
import { trackPageView, flushPendingEcommercePurchases } from '@/utils/analytics'
import { fetchPaymentOrders } from '@/utils/paymentsApi'

import {
    logout as logoutRequest,
    generateDatalensToken,
    getLastDatasetUpdateDate,
} from '@/utils/requests'

const store = useChatStore()
const access = store.access
const vuexStore = useVuexStore()
const router = useRouter()
const route = useRoute()

const panelRef = ref(null)
const isReportsView = computed(() => route.name === 'Reports')
const lastUpdate = ref('')
const isPricingOpen = ref(false)
const isPlanConfigOpen = ref(false)
const pendingPlan = ref({
    code: '',
    name: '',
    billingPeriod: 'month',
    regionSelectionEnabled: false,
    sourceSelectionEnabled: false,
})
const isSettingsOpen = ref(false)
const isTicketsOpen = ref(false)
const settingsSection = ref('profile')
const ticketsCategory = ref('')
const supportUnread = useSupportUnread()
const shareModalOpen = ref(false)
const publishModalOpen = ref(false)
const exportModalOpen = ref(false)
// БД-id выбранных сообщений для экспорта: страница /chat/print дёргает
// /api/chat/threads/{id}/history и фильтрует ответ по этим id.
const selectedServerIdsForExport = ref([])
const mySharesOpen = ref(false)
const myExportsOpen = ref(false)
const selectedServerIds = ref([])
const reports = ref([])
const reportsLoading = ref(true)

const editingMessage = computed(() => {
    const id = store.state.editingMessageId
    if (!id) return null
    return store.state.messages.find(m => m.id === id) || null
})

const REGEN_BLOCK_MESSAGES = {
    streaming: 'Дождитесь завершения текущей генерации, затем нажмите «Применить» снова.',
    limit: 'Лимит запросов исчерпан. Обновите план или попробуйте позже.',
    no_user_message: 'Не найден исходный запрос для перегенерации.',
    not_found: 'Сообщение не найдено.',
}

const regenerateApplyError = computed(() => {
    const reason = store.state.regenerateBlockReason
    return reason ? (REGEN_BLOCK_MESSAGES[reason] || '') : ''
})

const ROLE_ADMIN = 2
const isAdmin = computed(() => Number(userInfo.value?.role_id) === ROLE_ADMIN)

const currentPlan = computed(() => {
    if (!access.isAuthenticated.value) return 'free'
    return access.plan.value || 'free'
})

function openPricing() {
    // Виртуальная страница «plans» для Метрики: пользователь открыл модалку
    // с тарифами (кнопка «Обновить подписку» и т.п.). Внутренние действия
    // (клик по тарифу/покупка) уже трекаются отдельно через ecommerce.
    trackPageView('/chat/plans')
    isPricingOpen.value = true
}


function closePricing() {
    isPricingOpen.value = false
}

function openSettings(section = 'profile') {
    settingsSection.value = section
    isSettingsOpen.value = true
}

function closeSettings() {
    isSettingsOpen.value = false
}

function openSupport(category = '') {
    // Виртуальная страница «tickets» для Метрики: пользователь открыл модалку
    // «Обращения / поддержка». Трекаем только сам факт открытия, без
    // внутренних действий (создание/ответ на тикет).
    trackPageView('/chat/tickets')
    ticketsCategory.value = category
    isTicketsOpen.value = true
}


function closeTickets() {
    isTicketsOpen.value = false
    ticketsCategory.value = ''
    supportUnread.refreshUnread()
}

function onOpenTicketsFromSettings(category) {
    closeSettings()
    openSupport(category || 'billing')
}

function onSettingsUpgrade() {
    closeSettings()
    openPricing()
}

async function onPlanSelect(selection) {
    closePricing()
    // Модалка отдаёт { code, billingPeriod, name?, region_selection_enabled?, source_selection_enabled? }.
    const planCode = typeof selection === 'string' ? selection : selection?.code
    const billingPeriod = typeof selection === 'string' ? 'month' : (selection?.billingPeriod || 'month')
    if (!access.isAuthenticated.value || !planCode) return

    const needsRegions = Boolean(selection?.region_selection_enabled)
    const needsSources = Boolean(selection?.source_selection_enabled)
    if (needsRegions || needsSources) {
        pendingPlan.value = {
            code: planCode,
            name: selection?.name || planCode,
            billingPeriod,
            regionSelectionEnabled: needsRegions,
            sourceSelectionEnabled: needsSources,
        }
        isPlanConfigOpen.value = true
        return
    }

    try {
        await createChatPlanOrder(planCode, billingPeriod)
    } catch (error) {
        console.error('Plan order failed:', error)
    }
}

function closePlanConfig() {
    isPlanConfigOpen.value = false
}

async function onPlanConfigConfirm({ code, billingPeriod, config }) {
    closePlanConfig()
    try {
        await createChatPlanOrder(code, billingPeriod, config)
    } catch (error) {
        console.error('Plan order with regions failed:', error)
    }
}

function formatDate(date) {
    return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    })
}

async function loadLastUpdate() {
    if (!access.isAuthenticated.value) {
        lastUpdate.value = ''
        return
    }
    try {
        const token = localStorage.getItem('token')
        const tokenResponse = await generateDatalensToken(token)
        const tokenJsonData = await tokenResponse.json()
        if (!tokenResponse.ok || !tokenJsonData.data?.token) {
            throw new Error('token-error')
        }
        const datalensToken = tokenJsonData.data.token
        const response = await getLastDatasetUpdateDate(token, datalensToken)
        const responseJsonData = await response.json()
        if (!response.ok) {
            throw new Error('date-error')
        }
        lastUpdate.value = `Обновлено ${formatDate(new Date(responseJsonData.data.last_update_date))}`
    } catch (error) {
        lastUpdate.value = `Обновлено ${formatDate(new Date())}`
    }
}

async function applyPromptFromQuery() {
    const raw = route.query.prompt ?? route.query.q
    if (!raw || typeof raw !== 'string') return
    const prompt = raw.trim()
    if (!prompt || !store.state.activeThreadId) return

    await store.sendMessage(prompt)

    const nextQuery = { ...route.query }
    delete nextQuery.prompt
    delete nextQuery.q
    router.replace({ name: 'Chat', query: nextQuery })
}

// Прямая ссылка с запросом без указания треда (напр. из embed-виджета):
// стартуем новый диалог и сразу отправляем prompt в полной версии.
async function applyStandalonePrompt() {
    if (route.query.thread) return
    const raw = route.query.prompt ?? route.query.q
    if (!raw || typeof raw !== 'string' || !raw.trim()) return
    await store.startNewDialog()
    await applyPromptFromQuery()
}

async function syncThreadFromQuery() {
    const target = route.query.thread
    if (!target || typeof target !== 'string') return
    const promptPending = Boolean(
        (route.query.prompt && String(route.query.prompt).trim())
        || (route.query.q && String(route.query.q).trim()),
    )
    if (store.state.activeThreadId === target && !promptPending) return
    // Тред мог появиться только что (fork публичного отчёта / share); если его нет в кэше — обновим.
    if (!store.state.threads.some(t => t.id === target)) {
        try { await store.loadThreads() } catch (e) { /* ignore */ }
    }
    if (store.state.threads.some(t => t.id === target)) {
        await store.selectThread(target)
        await applyPromptFromQuery()
    }
}

onMounted(async () => {
    access.refreshAuthState()

    if (!access.isAuthenticated.value) {
        reportsLoading.value = false
        reports.value = []
        return
    }
    loadLastUpdate()
    loadReports()
    // Освежаем userInfo с бэка — нужно, чтобы пункт «Админ-раздел» в сайдбаре
    // появлялся сразу после повышения role_id, без релогина.
    vuexStore.dispatch('main/refreshCurrentUser')
    // Сначала тянем список сохранённых веток, чтобы сайдбар сразу заполнился.
    await store.loadThreads()
    // Дальше — если в URL ?thread=<id>, переключимся на него (фокус после fork
    // публичного отчёта/share или прямой ссылки).
    await syncThreadFromQuery()
    // Если пришли с ?prompt= без треда (handoff из embed) — новый диалог + отправка.
    await applyStandalonePrompt()
    // Возврат с оплаты робокассы: платёжный шлюз редиректит юзера на /chat
    // (returnUrl из createChatPlanOrder). PHP-webhook уже пометил заказ paid
    // на бэке — здесь допинываем `ecommerce purchase` в Метрику, потому что
    // из webhook браузер пользователя недоступен. Идемпотентно — де-дуп по
    // transactionId живёт в localStorage (см. flushPendingEcommercePurchases).
    if (access.isAuthenticated.value) {
        try {
            await flushPendingEcommercePurchases(fetchPaymentOrders)
        } catch (error) {
            console.error('[analytics] flush purchases failed:', error)
        }
    }
})


watch(
    () => route.fullPath,
    () => {
        access.refreshAuthState()
        if (!access.isAuthenticated.value) return
        loadLastUpdate()
        syncThreadFromQuery()
        applyStandalonePrompt()
    }
)

watch(
    () => access.isAuthenticated.value,
    async isAuth => {
        if (!isAuth) return
        loadLastUpdate()
        // После логина перезагружаем треды под нового пользователя.
        await store.loadThreads()
    }
)

async function loadReports() {
    reportsLoading.value = true
    try {
        reports.value = await getReports()
    } catch (error) {
        reports.value = []
    } finally {
        reportsLoading.value = false
    }
}

const threads = computed(() => store.state.threads)

const userInfo = computed(() => vuexStore.getters['main/userInfo'] || {})

const userName = computed(() => {
    const info = userInfo.value
    if (info?.name) {
        return info.name
    }
    return access.isAuthenticated.value ? 'Аналитик' : 'Гость'
})

const userInitials = computed(() => {
    const source = userName.value || ''
    const parts = source.split(' ').filter(Boolean)
    if (parts.length === 0) return 'AM'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
})

const userSubtitle = computed(() => {
    const company = (userInfo.value?.company || '').trim()
    return company
})

const activeThreadTitle = computed(() => {
    const active = store.activeThread.value
    return active?.title || 'Новый диалог'
})

const activeThreadSubtitle = computed(() => {
    const active = store.activeThread.value
    if (active && active.id !== 'thread-haval-chery') {
        return active.timeLabel || 'автосохранение'
    }
    if (store.state.messages.length > 0) {
        return `${store.state.messages.filter(m => m.role === 'ai').length} ответов · автосохранение`
    }
    return '12 ответов · автосохранение'
})

async function onNewDialog() {
    // «Режим» страницы определяется роутом (isReportsView), а не стором:
    // находясь на /reports, нужно сначала вернуться на /chat, иначе стор
    // обновится, а видна всё равно останется ReportsContent. Дожидаемся
    // навигации и рендера ChatConversationPanel, иначе panelRef ещё null.
    if (isReportsView.value) {
        await router.push({ name: 'Chat' })
        await nextTick()
    }
    store.startNewDialog()
    panelRef.value?.focusInput()
}

async function onSelectThread(thread) {
    if (isReportsView.value) {
        await router.push({ name: 'Chat' })
    }
    store.selectThread(thread.id)
}

function onThreadsSearch(search) {
    store.loadThreads({ reset: true, search })
}

async function onDeleteSavedThread(thread) {
    if (!thread?.id) return
    const label = thread.title ? `«${thread.title}»` : 'эту ветку'
    if (!window.confirm(`Удалить ${label}? Это действие нельзя отменить.`)) return
    await store.deleteThread(thread.id)
}

function onOpenReport(report) {
    if (!report?.token) return
    // Виртуальная страница «reports» для Метрики: аналитику важно ловить факт
    // клика по готовому отчёту в сайдбаре, а не только сам переход на страницу
    // PublicReport (та даст свой обычный hit после навигации).
    trackPageView('/chat/reports')
    router.push({ name: 'PublicReport', params: { token: report.token } })
}

function onSuggest(text, template = null) {
    panelRef.value?.setInputText(text)
    // Персональный шаблон несёт ещё и формат ответа — запоминаем его до отправки.
    if (template) store.applyPromptTemplate(template)
}

function onFollowup(message, action) {
    if (action?.label) {
        panelRef.value?.setInputText(action.label)
    }
}

function onUpdateCardView({ messageId, cardIndex, viewConfig }) {
    store.updateCardViewConfig(messageId, cardIndex, viewConfig)
}

function onRegenerate(messageId) {
    if (messageId) {
        store.regenerateAiMessage(messageId)
    } else {
        store.regenerateLast()
    }
}

function onEditMessage(messageId) {
    store.openMessageEditor(messageId)
}

async function onApplyMessageEdit({
    messageId,
    chartFormats,
    extraInstructions,
    responseFormats,
    responseFormatLabel,
}) {
    await store.regenerateAiMessage(messageId, {
        chartFormats,
        extraInstructions,
        responseFormats,
        responseFormatLabel,
    })
}

function onCopy() {
    // Копирование обрабатывается внутри ChatMessage.vue (navigator.clipboard)
}

function onFeedback(message, value) {
    if (!message?.id) return
    store.setFeedback(message.id, value)
}

function onStartShare() {
    if (!store.state.activeThreadId) return
    store.startSelection('share')
}

function onStartPublish() {
    if (!isAdmin.value || !store.state.activeThreadId) return
    store.startSelection('publish')
}

function onConfirmSelection() {
    const kind = store.state.selection.kind
    const ids = store.getSelectedServerIds()
    if (ids.length === 0) return
    if (kind === 'export') {
        // Печатная страница живёт в отдельной вкладке — ей нужны БД-id,
        // потому что она загрузит историю треда заново и сопоставит по serverId.
        selectedServerIdsForExport.value = ids
        store.cancelSelection()
        exportModalOpen.value = true
        return
    }
    selectedServerIds.value = ids
    store.cancelSelection()
    if (kind === 'share') shareModalOpen.value = true
    else if (kind === 'publish') publishModalOpen.value = true
}

function closeShare() {
    shareModalOpen.value = false
    selectedServerIds.value = []
}

function closePublish() {
    publishModalOpen.value = false
    selectedServerIds.value = []
}

function onShareCreated() {
    // Сохраним намёк, что у пользователя теперь есть свежая share-ссылка —
    // сайдбарная секция «Мои ссылки» подтянется при следующем открытии.
}

function onReportPublished() {
    // После публикации перечитываем sidebar-список «Готовых отчётов».
    loadReports()
}

function onExport() {
    if (!store.state.activeThreadId) return
    store.startSelection('export')
}

function closeExport() {
    exportModalOpen.value = false
    selectedServerIdsForExport.value = []
}

function onExported() {
    // Аналитика: считаем удачные экспорты отдельным виртуальным hit'ом,
    // чтобы видеть в Метрике конверсию «открыл чат → экспортировал в PDF».
    trackPageView('/chat/export')
}

async function onRenameThread(nextTitle) {
    if (!store.state.activeThreadId) return
    const value = (nextTitle || '').trim()
    if (!value) return
    await store.renameActiveThread(value)
}

async function onDeleteThread() {
    if (!store.state.activeThreadId) return
    const active = store.activeThread.value
    await onDeleteSavedThread(active || { id: store.state.activeThreadId })
}

function openMyShares() {
    // Виртуальная страница «links» для Метрики — открытие своих
    // расшаренных отчётов из сайдбара (без смены реального URL).
    trackPageView('/chat/links')
    mySharesOpen.value = true
}

function openMyExports() {
    // Виртуальная страница «exports» — по симметрии с openMyShares.
    trackPageView('/chat/exports')
    myExportsOpen.value = true
}

function goToAdmin() {
    router.push({ name: 'AdminDashboard' })
}

async function onLogout() {
    try {
        const token = localStorage.getItem('token')
        if (token) {
            await logoutRequest(token)
        }
    } catch (error) {
        // ignore
    } finally {
        localStorage.clear()
        await vuexStore.dispatch('main/logout')
        access.refreshAuthState()
        router.push('/chat')
    }
}
</script>

<style scoped>
.chat-page {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: white;
}

.chat-shell {
    flex: 1;
    display: flex;
    min-height: 0;
    position: relative;
    background: white;
    overflow: hidden;
}

.sidebar-backdrop {
    display: none;
}

@media (max-width: 1024px) {
    .sidebar-backdrop {
        display: block;
        position: fixed;
        inset: 0;
        background: rgba(21, 26, 34, 0.34);
        z-index: 40;
    }
}
</style>
